import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  Activity,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";
import { useAuth, useUser } from "@clerk/react";
import { Button } from "@/components/ui/button";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface InvitationPreview {
  status: "pending" | "accepted" | "cancelled" | "expired";
  patientEmail: string;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientName: string;
  welcomeNote: string | null;
  expiresAt: string;
  practiceName: string | null;
  practiceType: "nhs" | "private" | "independent" | null;
  hasExistingAccount: boolean;
}

function useQueryParam(name: string): string | null {
  return useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get(name);
  }, [name]);
}

export default function InvitationAccept() {
  const token = useQueryParam("token");
  const [, setLocation] = useLocation();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState<{ practiceName: string | null; redirectPath: string } | null>(null);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setError("This invitation link is missing its token.");
      setLoading(false);
      return;
    }
    fetch(`${BASE}/api/invitations/by-token/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return r.json() as Promise<InvitationPreview>;
      })
      .then((d) => setPreview(d))
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load invitation"))
      .finally(() => setLoading(false));
  }, [token]);

  const signedInEmail = user?.emailAddresses[0]?.emailAddress?.toLowerCase() ?? null;
  const allSignedInEmails = (user?.emailAddresses ?? []).map((e) =>
    e.emailAddress.toLowerCase(),
  );
  const inviteEmail = preview?.patientEmail.toLowerCase() ?? null;
  const emailMatches = inviteEmail !== null && allSignedInEmails.includes(inviteEmail);

  const accept = async () => {
    if (!token) return;
    setAccepting(true);
    setError(null);
    try {
      const res = await fetch(
        `${BASE}/api/invitations/accept/${encodeURIComponent(token)}`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { practiceName: string | null; redirectPath: string };
      localStorage.setItem("rheumhub_role", "patient");
      setAccepted(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  // Auto-accept once signed in with the matching email.
  useEffect(() => {
    if (!preview || preview.status !== "pending") return;
    if (!authLoaded || !isSignedIn) return;
    if (!emailMatches) return;
    if (attemptedRef.current || accepted) return;
    attemptedRef.current = true;
    void accept();
  }, [preview, authLoaded, isSignedIn, emailMatches]);

  const goToSignIn = (mode: "in" | "up") => {
    if (!preview) return;
    // Bounce through Clerk with a redirect back to this same accept URL so the
    // auto-accept effect fires once the user is signed in.
    const redirect = window.location.pathname + window.location.search;
    const params = new URLSearchParams();
    params.set("redirect_url", redirect);
    if (inviteEmail) params.set("email_address", inviteEmail);
    const path = mode === "up" ? "/sign-up" : "/sign-in";
    setLocation(`${path}?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-8 max-w-md w-full text-center space-y-4">
        <div className="bg-teal-50 p-3 rounded-2xl inline-block">
          <Activity className="h-6 w-6 text-teal-700" />
        </div>

        {loading && (
          <>
            <h1 className="text-xl font-bold text-slate-900">Checking your invitation…</h1>
            <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
          </>
        )}

        {!loading && error && !preview && (
          <>
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
            <h1 className="text-xl font-bold text-slate-900">Invitation unavailable</h1>
            <p className="text-sm text-slate-500">{error}</p>
            <Button variant="outline" onClick={() => setLocation("/")} className="w-full">
              Back to home
            </Button>
          </>
        )}

        {preview && !accepted && (
          <>
            {preview.status === "expired" && (
              <>
                <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
                <h1 className="text-xl font-bold text-slate-900">This invitation has expired</h1>
                <p className="text-sm text-slate-500">
                  Please ask {preview.practiceName ?? "your practice"} to send you a new
                  invitation link.
                </p>
              </>
            )}

            {preview.status === "accepted" && (
              <>
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
                <h1 className="text-xl font-bold text-slate-900">Already accepted</h1>
                <p className="text-sm text-slate-500">
                  This invitation has already been used. Sign in to access your patient
                  portal.
                </p>
                <Button onClick={() => setLocation("/sign-in")} className="w-full">
                  Sign in
                </Button>
              </>
            )}

            {preview.status === "cancelled" && (
              <>
                <AlertCircle className="h-10 w-10 text-slate-500 mx-auto" />
                <h1 className="text-xl font-bold text-slate-900">Invitation cancelled</h1>
                <p className="text-sm text-slate-500">
                  This invitation was cancelled by the practice.
                </p>
              </>
            )}

            {preview.status === "pending" && (
              <>
                <h1 className="text-xl font-bold text-slate-900">
                  You've been invited to RheumHub
                </h1>
                <p className="text-sm text-slate-600">
                  <strong>{preview.practiceName ?? "Your rheumatology practice"}</strong> has
                  invited <strong>{preview.patientName}</strong> ({preview.patientEmail}) to
                  join their practice on RheumHub.
                </p>
                {preview.welcomeNote && (
                  <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 text-left">
                    <p className="text-xs font-semibold text-teal-800 uppercase tracking-wide mb-1">
                      A note from your practice
                    </p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {preview.welcomeNote}
                    </p>
                  </div>
                )}
                <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Single-use link · expires{" "}
                  {new Date(preview.expiresAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>

                {!authLoaded && (
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
                )}

                {authLoaded && !isSignedIn && (
                  <div className="space-y-2">
                    {preview.hasExistingAccount ? (
                      <>
                        <p className="text-xs text-slate-500">
                          We found an existing RheumHub account for this email. Sign in to
                          link this practice to your account.
                        </p>
                        <Button
                          onClick={() => goToSignIn("in")}
                          className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                          data-testid="invite-sign-in"
                        >
                          Sign in &amp; join {preview.practiceName ?? "practice"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-slate-500">
                          Create your RheumHub account in a minute, using the email this
                          invitation was sent to.
                        </p>
                        <Button
                          onClick={() => goToSignIn("up")}
                          className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                          data-testid="invite-sign-up"
                        >
                          Create account &amp; accept
                        </Button>
                        <button
                          type="button"
                          onClick={() => goToSignIn("in")}
                          className="text-xs text-teal-700 hover:underline"
                        >
                          Already have an account? Sign in
                        </button>
                      </>
                    )}
                  </div>
                )}

                {authLoaded && isSignedIn && !emailMatches && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-left space-y-2">
                    <p className="text-xs text-amber-900">
                      You're signed in as <strong>{signedInEmail}</strong>, but this
                      invitation was sent to <strong>{preview.patientEmail}</strong>. Sign
                      out and back in with the correct email to accept.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => goToSignIn("in")}
                      className="w-full"
                    >
                      Sign in with {preview.patientEmail}
                    </Button>
                  </div>
                )}

                {authLoaded && isSignedIn && emailMatches && (
                  <Button
                    onClick={accept}
                    disabled={accepting}
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                    data-testid="invite-accept-confirm"
                  >
                    {accepting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Linking…
                      </>
                    ) : (
                      `Join ${preview.practiceName ?? "practice"}`
                    )}
                  </Button>
                )}

                {error && (
                  <p
                    role="alert"
                    className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5"
                  >
                    {error}
                  </p>
                )}
              </>
            )}
          </>
        )}

        {accepted && (
          <>
            <CheckCircle2 className="h-10 w-10 text-teal-600 mx-auto" />
            <h1 className="text-xl font-bold text-slate-900">
              You're linked to {accepted.practiceName ?? "your practice"}
            </h1>
            <p className="text-sm text-slate-500">
              Welcome to RheumHub. We've sent a welcome email with a quick overview of
              what's available in your patient portal.
            </p>
            <Button
              onClick={() => setLocation(accepted.redirectPath)}
              className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="invite-go-to-portal"
            >
              Go to my patient portal
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
